import api from '@/api';
import { dateFormat, saveParamsToQuery } from '@/utils/helpers';
import supplyTask from '@/api/supplyTask';

let currentTicketListRequest = null;

const supplyRequest = {
  namespaced: true,

  state() {
    return {
      isLoading: false,
      request: {},
      customer: {},
      contractor: {},
      supplier: {},
      storageId: null,
      storage: null,
      nomenclatures: [],
      objectsList: [],
      objectId: null,
      items: [],
      files: [],
      contractorComment: '',
      customerComment: '',
      itemsErrors: [],
      list: {
        supplyRequests: [],
        pageCount: 0,
        page: 0,
      },
    };
  },

  mutations: {
    changeLoading(state, status) {
      state.isLoading = status;
    },
    setRequest(state, data) {
      state.request = data;
    },
    setCustomer(state, data) {
      state.customer = data;
    },
    setContractor(state, data) {
      state.contractor = data;
    },
    setSupplier(state, data) {
      state.supplier = data;
    },
    setFiles(state, files) {
      state.files = files;
    },
    setContractorComment(state, text) {
      state.contractorComment = text;
    },
    setCustomerComment(state, text) {
      state.customerComment = text;
    },
    setObjectsList(state, list) {
      state.objectsList = list;
    },
    setObjectId(state, id) {
      state.objectId = id;
    },
    setStorageId(state, id) {
      state.storageId = id;
    },
    setStorage(state, storage) {
      state.storage = storage;
    },
    setNomenclatures(state, data) {
      state.nomenclatures = data || [];
    },
    setRequestStatus(state, status) {
      if (!state.request.id) {
        console.error('Нельзя изменить статус для несуществующего request');
        return;
      }
      state.request.status = status;
    },
    setPage(state, page) {
      state.list.page = page;
    },
    setSearch(state, value) {
      state.search = value;
    },
    setList(state, data) {
      state.list = { ...data.result };
    },
    setItemsErrors(state, error) {
      state.itemsErrors = error;
    },
    setSupplyRequestResult(state, result) {
      state.request = result.request;
      state.customer = result.customer;
      state.contractor = result.contractor;
      state.supplier = result.supplier;
      state.storageId = result.storage.id;
      state.objectId = result.object?.id;
      state.objectsList = [{ ...result?.object, storages: [result.storage] }];
      state.nomenclatures = result.nomenclatures || [];
      state.items = result.items;
    },
    resetDetailsState(state) {
      state.request = {};
      state.customer = {};
      state.contractor = {};
      state.objectId = null;
      state.storageId = null;
      state.nomenclatures = [];
      state.itemsErrors = [];
    },
  },
  actions: {
    async saveSupplyTask({ state, commit, rootState }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;

        if (!isLoggedIn) {
          return;
        }
        const params = {
          id: state.request.id,
          items: state.items.map((item) => ({ ...item.task, files: state.files })),
        };
        const { data } = await api.supplyTask.save(params);
        commit('setItemsErrors', []);
      } catch (e) {
        if (e.response.data?.description) {
          commit('setItemsErrors', [e.response.data.description]);
        }
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async saveSupply({
      state, commit, rootState,
    }, { items, isCreate }) {
      const isPermissibleRole = rootState.user.roles.some((item) => ['contractor'].includes(item.name));
      if (!state.objectId || !items?.length || !isPermissibleRole || state.itemsErrors.length) {
        return;
      }
      commit('changeLoading', true);
      items = items.filter((item) => item.id);
      try {
        const { isLoggedIn } = rootState.user;

        if (!isLoggedIn) {
          return;
        }
        const params = {
          requestId: state.request.id,
          status: isCreate ? 'Создана' : 'Черновик',
          objectId: state.objectId,
          storageId: state.storageId,
          items,
          comment: state.contractorComment,
        };
        const { data } = await api.supplyRequest.supplySave(params);
        if (!state.request.id) {
          window.history.replaceState({}, '', `/market/supply-request/${data.result.request.id}`);
        }
        commit('setSupplyRequestResult', data.result);
        commit('setItemsErrors', []);
      } catch (e) {
        if (e.response.data?.description) {
          commit('setItemsErrors', [e.response.data.description]);
        }
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadDetails({
      commit, rootState, rootGetters,
    }, { id, isTask }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!isLoggedIn || !role) {
          return;
        }

        let data = null;
        if (isTask) {
          data = (await api.supplyTask[role].details({ id })).data;

          if (rootGetters['user/hasRole']('supplier') || rootGetters['user/hasRole']('customer')) {
            data.result.items.forEach((item) => {
              item.deliveryDate = dateFormat(item.deliveryDate);
              item.sum = item.price * item.quantity;
              item.task = {
                id: item.id,
                supplierQuantity: item.quantity,
                supplierDeliveryDate: item.deliveryDate,
                supplierCancel: rootGetters['user/hasRole']('customer') ? item.contractorCancel : false,
                supplierCancelReason: rootGetters['user/hasRole']('customer') ? item.contractorCancelReason : '',
              };

              if (rootGetters['user/hasRole']('customer')) {
                item.deliveryDate = dateFormat(item.deliveryDateOrig);
                item.quantity = item.quantityOrig;
              }
            });
          }
        } else {
          data = (await api.supplyRequest[role].details({ requestId: id })).data;
        }

        commit('setSupplyRequestResult', data.result);
      } catch (e) {
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadObjectsList({ commit, rootState }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;

        if (!isLoggedIn) {
          return;
        }
        const { data } = await api.supplyRequest.objectsList();
        commit('setObjectsList', data.result);
      } catch (e) {
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadNomenclatures({ state, commit, rootState }) {
      if (!state.objectId) {
        return;
      }
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;

        if (!isLoggedIn) {
          return;
        }
        const { data } = await api.supplyRequest.nomenclatures({ objectId: state.objectId });
        commit('setNomenclatures', data.result);
      } catch (e) {
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async setObjectId({ commit }, id) {
      commit('setObjectId', id);
    },
    async setStorageId({ commit }, id) {
      commit('setStorageId', id);
    },
    async loadList({
      state, commit, rootState, rootGetters,
    }) {
      commit('changeLoading', true);

      try {
        if (currentTicketListRequest) {
          return;
        }
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('contractor');

        if (!role && isLoggedIn) {
          return;
        }

        const params = {
          pageSize: 10,
          page: state.list.page,
          search: state.search,
        };
        currentTicketListRequest = api.supplyRequest[role].list(params);
        const { data } = await currentTicketListRequest;
        saveParamsToQuery({ page: params.page, search: params.search });
        commit('setList', data);
        currentTicketListRequest = null;
      } catch (e) {
        currentTicketListRequest = null;
        console.log(e);
      }
      commit('changeLoading', false);
    },
    async setSearch({ commit, dispatch }, value) {
      commit('setSearch', value);
      commit('setPage', 1);
      await dispatch('loadList');
    },
    async setPage({ commit, dispatch }, page) {
      commit('setPage', page);
      await dispatch('loadList');
    },
    async acceptTask({ state, commit }) {
      commit('changeLoading', true);
      await api.supplyTask.changeStatusOnProcessedByReviewer({ id: state.request.id });
      commit('changeLoading', false);
    },
    async rejectTask({ state, commit }) {
      commit('changeLoading', true);
      await api.supplyTask.changeStatusOnCancel({ id: state.request.id });
      commit('changeLoading', false);
    },
    async vendorSelection({ state, commit }) {
      commit('changeLoading', true);
      await api.supplyRequest.vendorSelection({ id: state.request.id });
      commit('setRequestStatus', 'Ожидается выбор поставщика');
      commit('changeLoading', false);
    },
    async cancelRequest({ state, commit }) {
      commit('changeLoading', true);
      const params = {
        id: state.request.id,
        comment: state.customerComment,
      };
      await api.supplyRequest.cancel(params);
      commit('setRequestStatus', 'Отклонена');
      commit('changeLoading', false);
    },
  },

  getters: {
    getObject: (state) => {
      // eslint-disable-next-line no-restricted-globals
      const id = isNaN(state.objectId) ? state.objectId : Number(state.objectId);
      return state.objectsList?.find((object) => object.id === id);
    },
    getStorage: (state, getters) => {
      const id = state.storageId;
      return getters.getStoragesList?.find((storage) => storage.id === Number(id));
    },
    getStoragesList: (state, getters) => {
      const object = getters.getObject;
      return object?.storages;
    },
  },
};

export default supplyRequest;
